// ============================================================
// ArrangementGenerator.js — Smart Arrangement Structure Generator
//
// Given genre classification + loop role + analysis data, generates
// a complete arrangement blueprint with:
// - Section order and lengths (in bars)
// - Energy curve (how energy rises and falls)
// - Element guidance per section (what to add/remove)
// - Mix/production notes per section
//
// Arrangement templates are genre-specific and follow industry
// standards for each genre family. The generator adapts the template
// based on the analyzed loop's role and characteristics.
// ============================================================

import { LOOP_ROLES, ELEMENTS } from './LoopRoleInference.js';

// --- Section types that form an arrangement ---

export const SECTION_TYPES = {
  INTRO: 'intro',
  VERSE: 'verse',
  PRE_CHORUS: 'pre-chorus',
  CHORUS: 'chorus',
  DROP: 'drop',
  BUILDUP: 'buildup',
  BREAKDOWN: 'breakdown',
  BRIDGE: 'bridge',
  OUTRO: 'outro',
  // Electronic-specific
  FIRST_DROP: 'first-drop',
  SECOND_DROP: 'second-drop',
  BREAK: 'break',
};

/**
 * Genre-specific arrangement templates.
 * Each template defines a sequence of sections with bar lengths,
 * energy levels, and guidance for what elements to include.
 *
 * Energy is 0-1 where 0 = minimal and 1 = peak energy.
 * The template assumes the user's loop IS the peak energy section.
 */
const ARRANGEMENT_TEMPLATES = {
  // === ELECTRONIC / DANCE ===

  'electronic-standard': {
    name: 'Electronic Standard',
    description: 'Industry-standard electronic/dance arrangement. Two drops with breakdown between them.',
    totalBars: 128,
    sections: [
      { type: SECTION_TYPES.INTRO,      bars: 16, energy: 0.2, label: 'Intro',            guidance: 'Stripped-back. Establish the groove with minimal elements — a single rhythmic element or atmospheric texture. Set the key and hint at what\'s coming.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 16, energy: 0.5, label: 'Build',             guidance: 'Layer in elements progressively. Add hi-hats, then bass teases, then synth stabs. Use risers, snare rolls, or filter sweeps to create tension. Energy should steadily climb.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 1.0, label: 'Drop 1',            guidance: 'YOUR LOOP GOES HERE. Full energy — all elements firing. This is the payoff from the build. Make it hit hard on beat 1.' },
      { type: SECTION_TYPES.BREAKDOWN,  bars: 16, energy: 0.35, label: 'Breakdown',        guidance: 'Pull the kick and bass. Let the melody breathe. This is the emotional core — pads, vocals, or atmospheric elements. Create contrast with the drop.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 8,  energy: 0.6, label: 'Build 2',           guidance: 'Shorter build this time — the audience knows what\'s coming. Faster escalation, bigger risers. Tease the drop elements.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 1.0, label: 'Drop 2',            guidance: 'Second drop — same energy but with variation. Add a new element, change the bass pattern, or switch up the drums. Keep it fresh.' },
      { type: SECTION_TYPES.BREAKDOWN,  bars: 8,  energy: 0.25, label: 'Break',            guidance: 'Brief cooldown. Strip to atmosphere only. This gives the track breathing room before the final section.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 0.9, label: 'Final Drop',        guidance: 'Last hurrah. Can be a variation of Drop 1 or a combined version. Start peeling elements away in the last 4 bars to transition to outro.' },
      { type: SECTION_TYPES.OUTRO,      bars: 16, energy: 0.15, label: 'Outro',            guidance: 'Mirror the intro in reverse. Remove elements one by one. End with the same element you started with for a satisfying close. DJs need 16 bars to mix out.' },
    ]
  },

  'electronic-extended': {
    name: 'Electronic Extended (DJ Mix)',
    description: 'Extended arrangement optimized for DJ mixing. Longer intro/outro with clear mix points.',
    totalBars: 192,
    sections: [
      { type: SECTION_TYPES.INTRO,      bars: 32, energy: 0.15, label: 'Intro',            guidance: 'Long DJ-friendly intro. Start with just the kick or a single rhythmic element. Gradually add hi-hats and atmosphere. DJs will mix in over this section.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 16, energy: 0.4, label: 'Build 1',           guidance: 'Begin layering melodic and bass elements. Filter sweeps upward. Build anticipation with risers and tension elements.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 1.0, label: 'Drop 1',            guidance: 'YOUR LOOP. Full energy impact. Every element present and firing.' },
      { type: SECTION_TYPES.BREAK,      bars: 8,  energy: 0.5, label: 'Mini Break',        guidance: 'Brief pause — pull the kick for 4-8 bars. Keep the bass or a rhythmic element going. Resets energy for the next section.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 0.95, label: 'Drop 1B',          guidance: 'Variation of Drop 1. Change one key element — swap the hi-hat pattern, add a new percussion layer, or modify the bass line.' },
      { type: SECTION_TYPES.BREAKDOWN,  bars: 16, energy: 0.3, label: 'Breakdown',         guidance: 'The emotional center. Strip to pads, vocals, or atmospheric elements. Create maximum contrast with the drops. This is where you tell the story.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 16, energy: 0.6, label: 'Build 2',           guidance: 'Bigger build than the first. Layer more elements, use longer risers. The audience expects a massive payoff.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 1.0, label: 'Drop 2',            guidance: 'Second drop — the climax. New bass design, extra percussion, or a vocal chop layer. This should be the track\'s peak moment.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 0.85, label: 'Drop 2B',          guidance: 'Extended drop with elements starting to peel away in the last 8 bars. Transition gracefully toward the outro.' },
      { type: SECTION_TYPES.OUTRO,      bars: 32, energy: 0.1, label: 'Outro',             guidance: 'Long DJ-friendly outro. Reverse the intro — remove elements until just the kick remains. 32 bars gives DJs plenty of room to mix out.' },
    ]
  },

  'dnb-standard': {
    name: 'Drum & Bass Standard',
    description: 'Classic D&B arrangement. Fast pacing, two drops, breakdown in the middle.',
    totalBars: 128,
    sections: [
      { type: SECTION_TYPES.INTRO,      bars: 16, energy: 0.2, label: 'Intro',             guidance: 'Atmospheric start. A pad or reversed element to set the mood. Maybe a filtered breakbeat that hints at the full drum pattern. Keep it stripped back — the D&B energy comes later.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 8,  energy: 0.5, label: 'Build',              guidance: 'Introduce the bassline (filtered) and build the drum pattern. Use snare rolls and risers. D&B builds are typically shorter and more explosive than house.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 1.0, label: 'Drop 1',             guidance: 'YOUR LOOP. Full D&B energy — driving breakbeat, heavy sub bass, and any melodic/synth elements. The kick and snare pattern should be instantly infectious.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 0.9, label: 'Drop 1 (Variation)', guidance: 'Continue the energy but switch something up — new bass sound, different drum fill patterns, add or remove a melodic element. Keep the energy high.' },
      { type: SECTION_TYPES.BREAKDOWN,  bars: 16, energy: 0.3, label: 'Breakdown',          guidance: 'Pull the drums and bass. Let the melodic elements breathe — pads, vocal samples, or atmospheric textures. This is the contrast moment. In D&B, breakdowns are often shorter and more intense than in house.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 8,  energy: 0.6, label: 'Build 2',            guidance: 'Quick build back to the drop. Resampled breakbeat building speed, sub bass rising in pitch, snare roll into the drop.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 1.0, label: 'Drop 2',             guidance: 'Second drop — peak of the track. Add a new element that wasn\'t in Drop 1 (new synth layer, vocal chop, different reese bass). Make it the definitive moment.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 0.85, label: 'Outro Drop',        guidance: 'Final drop section with elements gradually peeling away. Last 8 bars should strip to just drums and a fading pad. Set up the outro.' },
      { type: SECTION_TYPES.OUTRO,      bars: 16, energy: 0.15, label: 'Outro',             guidance: 'Wind down. Just the breakbeat and atmosphere, elements falling away. End with a crash cymbal or reversed hit. Keep it DJ-mixable.' },
    ]
  },

  'bass-house-standard': {
    name: 'Bass House Standard',
    description: 'Standard bass house arrangement. Heavy drops, vocal-driven breakdown.',
    totalBars: 128,
    sections: [
      { type: SECTION_TYPES.INTRO,      bars: 16, energy: 0.2, label: 'Intro',             guidance: 'Start with a groove element — shuffled hi-hats or a percussion pattern. Establish the house tempo and swing feel. Maybe a filtered vocal tease.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 16, energy: 0.5, label: 'Build',              guidance: 'Introduce the bass (filtered). Add a vocal hook or synth stab. Use a rising filter on the bass to build tension. Classic house build: hi-hats get busier, snare rolls in last 4 bars.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 1.0, label: 'Drop 1',             guidance: 'YOUR LOOP. Full bass house impact — heavy distorted bass, four-on-the-floor kick, and percussive groove. The bass sound IS the hook.' },
      { type: SECTION_TYPES.BREAKDOWN,  bars: 16, energy: 0.3, label: 'Breakdown',          guidance: 'Pull the bass and kick. Vocal section or melodic interlude. This is the sing-along moment — the part people remember between the drops. Keep a light groove going.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 8,  energy: 0.6, label: 'Build 2',            guidance: 'Shorter, more intense build. The crowd knows what\'s coming. Pitch-rising bass, snare roll, and a big riser. Maybe a vocal "1, 2, 3, let\'s go" type element.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 1.0, label: 'Drop 2',             guidance: 'Second drop — variation of Drop 1. Switch the bass pattern (half-time for 8 bars then back?), add a new percussion element, or layer a vocal chop on top.' },
      { type: SECTION_TYPES.BREAK,      bars: 8,  energy: 0.35, label: 'Break',             guidance: 'Quick break — just kick and atmosphere for 8 bars. Resets for the final section.' },
      { type: SECTION_TYPES.DROP,       bars: 16, energy: 0.9, label: 'Final Drop',         guidance: 'Last drop. Combine the best elements from both drops. Start stripping in the last 4 bars to set up the outro.' },
      { type: SECTION_TYPES.OUTRO,      bars: 16, energy: 0.15, label: 'Outro',             guidance: 'Reverse the intro. Just the groove and atmosphere. End clean for DJ mixing.' },
    ]
  },

  // === HIP-HOP / RAP ===

  'hiphop-standard': {
    name: 'Hip-Hop Standard',
    description: 'Classic hip-hop song structure. Verse-chorus-verse with bridge.',
    totalBars: 96,
    sections: [
      { type: SECTION_TYPES.INTRO,      bars: 8,  energy: 0.3, label: 'Intro',             guidance: 'Set the mood. A melodic sample, atmospheric chords, or the main loop with a filter. 4-8 bars to establish the vibe before vocals come in.' },
      { type: SECTION_TYPES.VERSE,      bars: 16, energy: 0.5, label: 'Verse 1',           guidance: 'Main beat drops in but keep it relatively stripped. Kick, snare, hi-hats, and bass. Leave room for vocals. The beat supports the verse — don\'t overpower.' },
      { type: SECTION_TYPES.CHORUS,     bars: 8,  energy: 0.8, label: 'Chorus / Hook',     guidance: 'YOUR LOOP at full energy. This is the hook — the memorable part. Layer up: add melodic elements, doubled-up drums, or a wider stereo image. Make it feel bigger than the verse.' },
      { type: SECTION_TYPES.VERSE,      bars: 16, energy: 0.5, label: 'Verse 2',           guidance: 'Back to the verse beat. Can be identical to Verse 1 or add one subtle new element to keep it fresh. The vocals carry the variation.' },
      { type: SECTION_TYPES.CHORUS,     bars: 8,  energy: 0.85, label: 'Chorus / Hook',    guidance: 'Hook again — same as before but slightly bigger. Maybe add a counter-melody or extra percussion. Repetition builds recognition.' },
      { type: SECTION_TYPES.BRIDGE,     bars: 8,  energy: 0.4, label: 'Bridge',            guidance: 'Change the pattern. Different chord progression, half-time drums, or a completely new vibe. This creates contrast and keeps the arrangement from being predictable.' },
      { type: SECTION_TYPES.CHORUS,     bars: 16, energy: 0.9, label: 'Final Chorus',      guidance: 'Extended final hook — double length for emphasis. This is the climax. Pull out all the stops: full arrangement, vocal ad-libs, extra percussion layers.' },
      { type: SECTION_TYPES.OUTRO,      bars: 16, energy: 0.2, label: 'Outro',             guidance: 'Fade the beat out or strip elements one by one. Can repeat the hook melody softly or end with the intro motif. Leave the listener satisfied.' },
    ]
  },

  'trap-standard': {
    name: 'Trap Standard',
    description: 'Modern trap arrangement with 808-heavy drops and atmospheric builds.',
    totalBars: 112,
    sections: [
      { type: SECTION_TYPES.INTRO,      bars: 8,  energy: 0.2, label: 'Intro',             guidance: 'Atmospheric. Melody or vocal sample with reverb. Maybe a pitched-down 808 hit. Set the dark/moody tone.' },
      { type: SECTION_TYPES.VERSE,      bars: 16, energy: 0.5, label: 'Verse 1',           guidance: 'Beat drops: 808 pattern, hi-hat rolls, snare on 3. Keep the 808 pattern simple. Leave headroom for vocals. The trap bounce should groove.' },
      { type: SECTION_TYPES.CHORUS,     bars: 8,  energy: 0.9, label: 'Hook',              guidance: 'YOUR LOOP. Full trap energy — 808 slides, layered hi-hats, and the melodic hook. This should be instantly catchy and bass-heavy.' },
      { type: SECTION_TYPES.VERSE,      bars: 16, energy: 0.55, label: 'Verse 2',          guidance: 'Same beat, maybe with an added element (new hi-hat pattern, ad-lib layer). Keep the 808 driving.' },
      { type: SECTION_TYPES.CHORUS,     bars: 8,  energy: 0.9, label: 'Hook',              guidance: 'Hook again. Consider adding a counter-melody or vocal chop that wasn\'t in the first hook for subtle evolution.' },
      { type: SECTION_TYPES.BRIDGE,     bars: 8,  energy: 0.35, label: 'Bridge',           guidance: 'Drop the 808 and drums. Melody or vocal-only section. Creates contrast. Maybe a half-time beat or totally different vibe.' },
      { type: SECTION_TYPES.BUILDUP,    bars: 4,  energy: 0.5, label: 'Build',             guidance: 'Quick build back — 808 slides rising, hi-hat roll speeding up. 4 bars is enough in trap.' },
      { type: SECTION_TYPES.CHORUS,     bars: 16, energy: 1.0, label: 'Final Hook',        guidance: 'Biggest version of the hook. Extended, with all layers active. The 808 should be hitting the hardest here.' },
      { type: SECTION_TYPES.OUTRO,      bars: 8,  energy: 0.15, label: 'Outro',            guidance: 'Melody fades. Maybe just the 808 and atmosphere for the last 4 bars. Quick, clean ending.' },
    ]
  },

  // === POP / R&B ===

  'pop-standard': {
    name: 'Pop Standard',
    description: 'Classic pop song structure. Verse-prechorus-chorus with bridge.',
    totalBars: 128,
    sections: [
      { type: SECTION_TYPES.INTRO,       bars: 8,  energy: 0.25, label: 'Intro',           guidance: 'Hook the listener immediately. A memorable melodic motif, vocal phrase, or rhythmic element. 4-8 bars that set up the song\'s identity.' },
      { type: SECTION_TYPES.VERSE,       bars: 16, energy: 0.4, label: 'Verse 1',          guidance: 'Stripped back to support vocals. Rhythmic foundation with light melodic elements. The production should enhance the vocal melody without competing.' },
      { type: SECTION_TYPES.PRE_CHORUS,  bars: 8,  energy: 0.6, label: 'Pre-Chorus',       guidance: 'Build energy. Add layers — busier drums, rising synths, or vocal harmonies. This is the ramp that makes the chorus feel like a release.' },
      { type: SECTION_TYPES.CHORUS,      bars: 16, energy: 0.9, label: 'Chorus',           guidance: 'YOUR LOOP. Full production — the hook should be undeniable. Wide stereo, all elements present, memorable melody. This is what people sing along to.' },
      { type: SECTION_TYPES.VERSE,       bars: 16, energy: 0.4, label: 'Verse 2',          guidance: 'Similar to Verse 1 but with one added element to maintain interest. The vocal performance usually evolves here even if the production stays similar.' },
      { type: SECTION_TYPES.PRE_CHORUS,  bars: 8,  energy: 0.65, label: 'Pre-Chorus',      guidance: 'Same build as before, maybe slightly bigger. The audience anticipates the chorus now — lean into that expectation.' },
      { type: SECTION_TYPES.CHORUS,      bars: 16, energy: 0.95, label: 'Chorus',          guidance: 'Second chorus — same structure but consider adding a harmony, extra percussion, or wider arrangement to make it feel like it\'s growing.' },
      { type: SECTION_TYPES.BRIDGE,      bars: 8,  energy: 0.35, label: 'Bridge',          guidance: 'Complete change. Different chords, different rhythm, different vocal approach. This is the surprise that makes the final chorus hit even harder.' },
      { type: SECTION_TYPES.CHORUS,      bars: 16, energy: 1.0, label: 'Final Chorus',     guidance: 'The climax. Everything at maximum. Double the chorus, add vocal ad-libs, extra layers. Last 4 bars can start winding down.' },
      { type: SECTION_TYPES.OUTRO,       bars: 16, energy: 0.2, label: 'Outro',            guidance: 'Reprise the intro motif or let the chorus melody fade. Clean ending or fade out — your choice. Leave the hook ringing in the listener\'s ear.' },
    ]
  },

  // === ROCK ===

  'rock-standard': {
    name: 'Rock Standard',
    description: 'Standard rock arrangement. Verse-chorus-verse with guitar solo bridge.',
    totalBars: 128,
    sections: [
      { type: SECTION_TYPES.INTRO,       bars: 8,  energy: 0.4, label: 'Intro',            guidance: 'Riff-driven or atmospheric intro. Establish the guitar tone and energy level. Can be a stripped version of the main riff or a separate melodic idea.' },
      { type: SECTION_TYPES.VERSE,       bars: 16, energy: 0.5, label: 'Verse 1',          guidance: 'Rhythm guitar and bass locked to the drums. Keep it tight and leave room for vocals. Clean or crunch tone — save the heavy distortion for the chorus.' },
      { type: SECTION_TYPES.PRE_CHORUS,  bars: 8,  energy: 0.65, label: 'Pre-Chorus',      guidance: 'Build tension. Open up the chord voicings, add cymbal rides, or introduce a new guitar part. Energy climbs toward the chorus.' },
      { type: SECTION_TYPES.CHORUS,      bars: 16, energy: 0.9, label: 'Chorus',           guidance: 'YOUR LOOP. Full band energy — power chords, driving bass, and open drums. The hook should be anthemic. Think singalong.' },
      { type: SECTION_TYPES.VERSE,       bars: 16, energy: 0.5, label: 'Verse 2',          guidance: 'Back to verse dynamics. Can add a second guitar part or subtle melodic embellishment. Rhythm section stays consistent.' },
      { type: SECTION_TYPES.PRE_CHORUS,  bars: 8,  energy: 0.7, label: 'Pre-Chorus',       guidance: 'Bigger than the first pre-chorus. Add harmonized vocals, busier drums, or an ascending bass line.' },
      { type: SECTION_TYPES.CHORUS,      bars: 16, energy: 0.95, label: 'Chorus',          guidance: 'Second chorus — add a guitar harmony, wider panning, or extra vocal harmony. Everything louder and more intense.' },
      { type: SECTION_TYPES.BRIDGE,      bars: 16, energy: 0.6, label: 'Bridge / Solo',    guidance: 'Guitar solo or completely different section. Change the chord progression. This is the creative wild card — surprise the listener. Build back to the final chorus.' },
      { type: SECTION_TYPES.CHORUS,      bars: 16, energy: 1.0, label: 'Final Chorus',     guidance: 'Biggest chorus. All guitars, full drum pattern, vocal ad-libs. Can extend or repeat. The definitive statement.' },
      { type: SECTION_TYPES.OUTRO,       bars: 8,  energy: 0.3, label: 'Outro',            guidance: 'Wind down. Reprise the intro riff, fade out on the chorus progression, or end with a crash. Make it decisive.' },
    ]
  },
};

/**
 * Maps genre names to their arrangement template key.
 */
const GENRE_TO_TEMPLATE = {
  'Drum & Bass': 'dnb-standard',
  'Liquid DnB': 'dnb-standard',
  'Neurofunk': 'dnb-standard',
  'Jungle': 'dnb-standard',
  'Bass House': 'bass-house-standard',
  'Deep House': 'electronic-standard',
  'Tech House': 'electronic-standard',
  'Techno': 'electronic-extended',
  'Melodic Techno': 'electronic-standard',
  'Trance': 'electronic-extended',
  'Progressive House': 'electronic-extended',
  'Dubstep': 'electronic-standard',
  'Riddim': 'electronic-standard',
  'Garage / UK Garage': 'electronic-standard',
  'Electro House': 'electronic-standard',
  'Future Bass': 'electronic-standard',
  'Hardstyle': 'electronic-standard',
  'Ambient': 'electronic-standard',
  'Downtempo': 'electronic-standard',
  'Boom Bap': 'hiphop-standard',
  'Trap': 'trap-standard',
  'Lo-Fi Hip Hop': 'hiphop-standard',
  'Drill': 'trap-standard',
  'Pop': 'pop-standard',
  'R&B': 'pop-standard',
  'Rock': 'rock-standard',
  'Punk Rock': 'rock-standard',
  'Metal': 'rock-standard',
  'Melodic Hardcore': 'rock-standard',
};

/**
 * Generate a complete arrangement from analysis results.
 *
 * @param {Object} analysis - Result from analyzeLoop()
 * @param {Object} genreResult - Result from classifyGenre()
 * @param {Object} roleResult - Result from inferLoopRole()
 * @param {Object} [options] - Override options
 * @param {string} [options.templateOverride] - Force a specific template key
 * @param {string} [options.genreOverride] - Force a specific genre name
 * @returns {{
 *   templateName: string,
 *   genre: string,
 *   bpm: number,
 *   key: string,
 *   totalBars: number,
 *   totalDuration: number,
 *   sections: Array<{
 *     type: string,
 *     label: string,
 *     bars: number,
 *     startBar: number,
 *     energy: number,
 *     guidance: string,
 *     isLoopSection: boolean,
 *     elements: string[]
 *   }>
 * }}
 */
export function generateArrangement(analysis, genreResult, roleResult, options = {}) {
  const genre = options.genreOverride || genreResult.primary.name;
  const templateKey = options.templateOverride || GENRE_TO_TEMPLATE[genre] || 'electronic-standard';
  const template = ARRANGEMENT_TEMPLATES[templateKey];

  if (!template) {
    throw new Error(`Unknown template: ${templateKey}`);
  }

  const bpm = analysis.bpm;
  const key = analysis.key;
  const loopBars = analysis.bars;
  const loopRole = roleResult.role;
  const loopElements = roleResult.elements;

  // Build section list with computed positions
  let currentBar = 1;
  const sections = template.sections.map((section, index) => {
    const startBar = currentBar;

    // Determine if this section should contain the user's loop
    const isLoopSection = isDropOrPeakSection(section, loopRole);

    // Infer which elements should be present in this section
    const elements = inferSectionElements(section, loopElements, template.sections, index);

    const result = {
      type: section.type,
      label: section.label,
      bars: section.bars,
      startBar,
      energy: section.energy,
      guidance: section.guidance,
      isLoopSection,
      elements,
    };

    currentBar += section.bars;
    return result;
  });

  // Calculate total duration
  const beatsPerBar = 4;
  const totalBars = sections.reduce((sum, s) => sum + s.bars, 0);
  const totalDuration = (totalBars * beatsPerBar * 60) / bpm;

  return {
    templateName: template.name,
    templateDescription: template.description,
    genre,
    bpm,
    key,
    loopBars,
    loopRole: roleResult.roleLabel,
    totalBars,
    totalDuration: Math.round(totalDuration),
    sections
  };
}

/**
 * Determines if a template section is where the user's loop should go.
 * Maps loop roles to section types.
 */
function isDropOrPeakSection(section, loopRole) {
  // The user's loop goes in the peak-energy section matching their role
  switch (loopRole) {
    case LOOP_ROLES.MAIN_DROP:
    case LOOP_ROLES.FULL_MIX:
      return section.type === SECTION_TYPES.DROP ||
             section.type === SECTION_TYPES.FIRST_DROP ||
             section.type === SECTION_TYPES.CHORUS;

    case LOOP_ROLES.BREAKDOWN:
      return section.type === SECTION_TYPES.BREAKDOWN;

    case LOOP_ROLES.BUILDUP:
      return section.type === SECTION_TYPES.BUILDUP ||
             section.type === SECTION_TYPES.PRE_CHORUS;

    case LOOP_ROLES.INTRO:
      return section.type === SECTION_TYPES.INTRO;

    case LOOP_ROLES.BASS_LOOP:
    case LOOP_ROLES.DRUM_BREAK:
      // Bass and drum loops are used throughout but are the
      // foundation in drop/chorus sections
      return section.type === SECTION_TYPES.DROP ||
             section.type === SECTION_TYPES.CHORUS;

    case LOOP_ROLES.MELODIC_HOOK:
      return section.type === SECTION_TYPES.CHORUS ||
             section.type === SECTION_TYPES.DROP ||
             section.type === SECTION_TYPES.BREAKDOWN;

    case LOOP_ROLES.PAD_ATMOSPHERE:
      return section.type === SECTION_TYPES.BREAKDOWN ||
             section.type === SECTION_TYPES.INTRO;

    default:
      return section.energy >= 0.8;
  }
}

/**
 * Infer which elements should be present in a given section,
 * based on what the loop contains and the section's energy level.
 *
 * The logic: peak sections have all loop elements.
 * Lower energy sections progressively strip elements.
 */
function inferSectionElements(section, loopElements, allSections, index) {
  const energy = section.energy;

  if (energy >= 0.8) {
    // Peak section: all elements
    return [...loopElements];
  }

  if (energy >= 0.5) {
    // Mid energy: most elements minus the heaviest
    return loopElements.filter(el =>
      el !== ELEMENTS.SUB_BASS &&
      el !== ELEMENTS.LEAD_SYNTH
    );
  }

  if (energy >= 0.3) {
    // Low-mid: atmospheric + rhythmic basics
    return loopElements.filter(el =>
      el === ELEMENTS.KICK ||
      el === ELEMENTS.HIHATS ||
      el === ELEMENTS.PAD ||
      el === ELEMENTS.FX ||
      el === ELEMENTS.BASS
    );
  }

  // Low energy: just atmosphere
  return loopElements.filter(el =>
    el === ELEMENTS.PAD ||
    el === ELEMENTS.FX ||
    el === ELEMENTS.STRINGS
  );
}

/**
 * Get all available arrangement templates for display in UI.
 * @returns {Array<{key: string, name: string, description: string, totalBars: number}>}
 */
export function getAvailableTemplates() {
  return Object.entries(ARRANGEMENT_TEMPLATES).map(([key, template]) => ({
    key,
    name: template.name,
    description: template.description,
    totalBars: template.totalBars,
    sectionCount: template.sections.length
  }));
}

/**
 * Get the recommended template for a genre.
 * @param {string} genreName
 * @returns {string} Template key
 */
export function getTemplateForGenre(genreName) {
  return GENRE_TO_TEMPLATE[genreName] || 'electronic-standard';
}
